import React, { MouseEventHandler } from 'react';
import componentProps, { ComponentProps } from '../../../helpers/componentProps';
import { applyComponentTheme } from '../../../theme';
import { BasePanelProps } from '../types';
import {
  AnimateHeightWithZIndex,
  BottomLabel,
  BottomLabelRichText,
  Container,
  CornerLabel,
  CornerLabelIcon,
  CornerLabelRichText,
  PanelBodySpacing,
  PanelStyleProps,
  StyledPanel,
} from './styles';

export type CornerLabelOptions = {
  html?: string;
  icon?: string;
};

export type PanelProps = BasePanelProps & {
  id: string;
  cornerLabel?: CornerLabelOptions;
  bottomLabelText?: string;
  bottomLabelRole?: string;
  bottomLabelAriaLive?: 'off' | 'assertive' | 'polite';
  bottomLabelId?: string;
  forwardAs?: React.ElementType | keyof JSX.IntrinsicElements;
  onClick?: MouseEventHandler;
  children?: React.ReactNode;
} & ComponentProps;

/**
 * A flexible panel component with optional color, corner label and bottom label.
 *
 * @param color - If not provided plain panel will render (bottomLabel and cornerLabel will not render).
 * @param colors.primaryColor - color for panel features (borders, cornerLabel.html, cornerLabel.icon). If not provided, bottomLabel and cornerLabel should not be provided and plain panel will render.
 * @param colors.lightColor - color for label backgrounds and inset border. Must meet acceptability criteria for contrast ratio with 'colors.primaryColor'.
 * @param cornerLabel - optional corner label.
 * @param cornerLabel.html - html to include in the cornerLabel. Use '<b>' tag to enlarge and embolden text.
 * @param cornerLabel.icon - name of optional icon to include.
 * @param bottomLabelText - optional bottom label with plain text.
 * @param borderShadows - optionally display inset colored (colors.lightColor) border shadow and outset grey border shadow
 */

const Panel: React.FC<PanelProps> = ({
  id,
  colors,
  borderShadows,
  cornerLabel,
  bottomLabelText,
  bottomLabelRole,
  bottomLabelAriaLive = 'polite',
  bottomLabelId = `${id}-bottom-label`,
  children,
  forwardAs,
  onClick,
  ...props
}) => {
  const bottomLabel = !!bottomLabelText && colors;

  const styleProps: PanelStyleProps = {
    colors,
    borderShadows,
    cornerLabel: cornerLabel
      ? {
          iconOnly: !!cornerLabel?.icon && !cornerLabel?.html,
          isDisplayed: !!colors,
        }
      : undefined,
  };

  const cornerLabelId = `${id}-corner-label`;

  return (
    <Container
      {...componentProps(props)}
      id={id}
      onClick={onClick}
      colors={colors}
      borderShadows={borderShadows}
      aria-describedby={`${cornerLabel ? cornerLabelId : ''} ${bottomLabelId}`}
      as={forwardAs}>
      <StyledPanel {...styleProps}>
        {cornerLabel && colors && (
          <CornerLabel id={cornerLabelId} {...styleProps}>
            {cornerLabel.html && <CornerLabelRichText html={cornerLabel?.html} />}
            {cornerLabel.icon && (
              <CornerLabelIcon
                name={cornerLabel.icon}
                size="medium"
                color={colors?.primaryColor}
              />
            )}
          </CornerLabel>
        )}
        <PanelBodySpacing {...styleProps}>{children}</PanelBodySpacing>
      </StyledPanel>
      <AnimateHeightWithZIndex
        duration={300}
        height={bottomLabel ? 'auto' : 0}
        animateOpacity
        easing={bottomLabel ? 'ease-out' : 'step-start'}>
        <BottomLabel
          role={bottomLabelRole}
          aria-live={bottomLabelAriaLive}
          {...styleProps}>
          <BottomLabelRichText id={bottomLabelId} html={bottomLabelText || ''} />
        </BottomLabel>
      </AnimateHeightWithZIndex>
    </Container>
  );
};

export default applyComponentTheme(Panel, 'Panel');
